import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../auth/AuthContext';
import GoogleSignInButton from '../components/GoogleSignInButton';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const REGISTERED_NAME_NOTICE = 'Use your real full name exactly as it appears on your bank account. Withdrawals are only paid to a bank account in the same name you register with.';
function isValidRegisteredName(value) { const n=String(value||'').trim().replace(/\s+/g,' '); return n.length>=5 && n.length<=120 && /^[\p{L}\p{M}]+(?:[ .'-]+[\p{L}\p{M}]+)*\.?$/u.test(n) && n.split(/\s+/).length>=2; }
function getInstallationId() { try { const k='9jawin_installation_id'; let id=window.localStorage.getItem(k); if (!id || !/^(?:[0-9a-f-]{36}|[0-9a-f]{32})$/i.test(id)) { id=window.crypto?.randomUUID ? window.crypto.randomUUID() : Array.from(window.crypto.getRandomValues(new Uint8Array(16)), b=>b.toString(16).padStart(2,'0')).join(''); window.localStorage.setItem(k,id); } return id; } catch (_) { return null; } }
function TurnstileWidget({ onVerify }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    const render = () => { if (window.turnstile && ref.current) window.turnstile.render(ref.current, { sitekey: TURNSTILE_SITE_KEY, callback: onVerify }); };
    if (window.turnstile) render(); else { const script=document.createElement('script'); script.src='https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'; script.async=true; script.defer=true; script.onload=render; document.head.appendChild(script); }
  }, [onVerify]);
  return TURNSTILE_SITE_KEY ? <div ref={ref} style={{margin:'0 0 16px'}} /> : null;
}

// ============================================================
// VALIDATION HELPERS
// ============================================================
const validateUsername = (value) => {
  if (!value.trim()) return 'Username is required.';
  if (value.length < 3) return 'Username must be at least 3 characters.';
  if (value.length > 30) return 'Username must be 30 characters or less.';
  if (!/^[a-zA-Z0-9_]+$/.test(value)) return 'Username can only contain letters, numbers, and underscores.';
  return '';
};

const validateEmail = (value) => {
  if (!value.trim()) return 'Email is required.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address.';
  if (value.length > 255) return 'Email is too long.';
  return '';
};

const validatePhone = (value, country) => {
  if (!value.trim()) return ''; // Optional field
  // Nigerian phone: 11 digits, may start with 0, 234, or +234
  if (country === 'NG') {
    const cleaned = value.replace(/[\s\-\(\)]/g, '');
    if (!/^(?:\+?234|0)?[789][01]\d{8}$/.test(cleaned)) {
      return 'Please enter a valid Nigerian phone number (e.g., 08012345678).';
    }
  } else {
    const cleaned = value.replace(/[\s\-\(\)\+\-]/g, '');
    if (cleaned.length < 7 || cleaned.length > 15) {
      return 'Please enter a valid phone number.';
    }
  }
  return '';
};

const validatePassword = (value) => {
  if (!value) return 'Password is required.';
  if (value.length < 8) return 'Password must be at least 8 characters.';
  if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter.';
  if (!/[0-9]/.test(value)) return 'Password must contain at least one number.';
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)) {
    return 'Password must contain at least one special character.';
  }
  return '';
};

const validateConfirmPassword = (value, password) => {
  if (!value) return 'Please confirm your password.';
  if (value !== password) return 'Passwords do not match.';
  return '';
};

// ============================================================
// REGISTER COMPONENT
// ============================================================
export default function Register() {
  const navigate = useNavigate();
  const { completeAuthentication } = useAuth();
  const [form, setForm] = useState({
    fullName: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    country: 'NG',
    referralCode: '',
    promoCode: '',
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [captchaToken, setCaptchaToken] = useState(null);
  const [googlePending, setGooglePending] = useState('');
  const [googleName, setGoogleName] = useState('');
  const [googleUseName, setGoogleUseName] = useState(true);
  const [googleFullName, setGoogleFullName] = useState('');
  const [googleCompletionLoading, setGoogleCompletionLoading] = useState(false);

  // --- Validate a single field ---
  const validateField = (name, value) => {
    switch (name) {
      case 'fullName': return isValidRegisteredName(value) ? '' : 'Enter your real full name using at least two words, with no numbers or symbols.';
      case 'username': return validateUsername(value);
      case 'email': return validateEmail(value);
      case 'phone': return validatePhone(value, form.country);
      case 'password': return validatePassword(value);
      case 'confirmPassword': return validateConfirmPassword(value, form.password);
      default: return '';
    }
  };

  // --- Handle input changes ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...form, [name]: value };

    // If password changes, re-validate confirmPassword
    if (name === 'password' && touched.confirmPassword) {
      const confirmErr = validateConfirmPassword(form.confirmPassword, value);
      setErrors((prev) => ({ ...prev, confirmPassword: confirmErr }));
    }

    // If country changes, re-validate phone
    if (name === 'country' && form.phone.trim()) {
      const phoneErr = validatePhone(form.phone, value);
      setErrors((prev) => ({ ...prev, phone: phoneErr }));
    }

    setForm(updated);

    // Live validation after field is touched
    if (touched[name] && name !== 'confirmPassword') {
      const err = validateField(name, name === 'country' ? undefined : value);
      setErrors((prev) => ({ ...prev, [name]: err }));
    }
  };

  // --- Mark field as touched (on blur) ---
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: err }));
  };

  // --- Validate all fields ---
  const validateAll = () => {
    const newErrors = {
      fullName: isValidRegisteredName(form.fullName) ? '' : 'Enter your real full name using at least two words, with no numbers or symbols.',
      username: validateUsername(form.username),
      email: validateEmail(form.email),
      phone: validatePhone(form.phone, form.country),
      password: validatePassword(form.password),
      confirmPassword: validateConfirmPassword(form.confirmPassword, form.password),
    };
    setErrors(newErrors);
    setTouched({
      fullName: true,
      username: true,
      email: true,
      phone: true,
      password: true,
      confirmPassword: true,
    });
    return !Object.values(newErrors).some(Boolean);
  };

  // --- Submit handler ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    if (!validateAll()) return;
    if (form.referralCode.trim() && form.promoCode.trim()) {
      setServerError('You can use either a referral code or a promo code, not both.');
      return;
    }
    if (TURNSTILE_SITE_KEY && !captchaToken) { setServerError('Please complete the security verification.'); return; }

    setLoading(true);
    try {
      const res = await api.auth.register({
        fullName: form.fullName.trim().replace(/\s+/g, ' '),
        deviceId: getInstallationId(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || undefined,
        username: form.username.trim(),
        password: form.password,
        country: form.country,
        referralCode: form.referralCode.trim().toUpperCase() || undefined,
        promoCode: form.promoCode.trim().toUpperCase() || undefined,
        captchaToken: captchaToken || undefined,
      });

      const registrationData = res.data?.data || {};
      if (registrationData.verificationRequired && registrationData.challengeId) {
        navigate('/verify-email?challengeId=' + encodeURIComponent(registrationData.challengeId), {
          replace: true,
          state: { email: registrationData.email || form.email.trim().toLowerCase(), expiresAt: registrationData.expiresAt || null },
        });
        return;
      }
      completeAuthentication(registrationData);
      navigate('/', { replace: true });
    } catch (err) {
      // Distinguish between different error types
      if (err.response) {
        // Server responded with an HTTP error
        const status = err.response.status;
        const msg = err.response?.data?.message;

        if (status === 400 && msg) {
          // Validation error from server
          setServerError(msg);
        } else if (status === 409 && msg) {
          // Conflict - username, email, or phone already exists
          setServerError(msg);
        } else if (status === 429) {
          // Rate limited
          setServerError(msg || 'Too many registration attempts. Please try again later.');
        } else if (status >= 500) {
          // Server error
          setServerError('A server error occurred. Please try again later.');
        } else {
          // Other HTTP errors
          setServerError(msg || 'Registration failed. Please try again.');
        }
      } else if (err.request) {
        // Request was made but no response received (network error)
        setServerError('Unable to connect to the server. Please check your connection and try again.');
      } else {
        // Something went wrong setting up the request
        setServerError('An unexpected error occurred. Please try again.');
        console.error('Registration error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // STYLES (matching existing dark theme)
  // ============================================================
  const colors = {
    bg: '#0a0a0a',
    card: '#111111',
    inputBg: '#1a1a1a',
    border: '#2a2a2a',
    borderFocus: '#00d4aa',
    primary: '#00d4aa',
    primaryHover: '#00b892',
    error: '#ff4d4d',
    errorBg: 'rgba(255, 77, 77, 0.1)',
    textPrimary: '#ffffff',
    textSecondary: '#888888',
    textMuted: '#666666',
  };

  const inputBase = {
    padding: '14px 16px',
    borderRadius: '10px',
    border: `1px solid ${colors.border}`,
    background: colors.inputBg,
    color: colors.textPrimary,
    fontSize: '15px',
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    fontFamily: 'inherit',
  };

  const selectBase = {
    ...inputBase,
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23888' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 14px center',
    paddingRight: '36px',
  };

  const fieldGap = '18px';
  const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };
  const errorStyle = {
    fontSize: '12px',
    color: colors.error,
    marginTop: '4px',
    minHeight: '16px',
  };

  const handleGoogleCompletion = async (e) => {
    e.preventDefault();
    setServerError('');
    const referralCode = form.referralCode.trim().toUpperCase();
    const promoCode = form.promoCode.trim().toUpperCase();
    if (referralCode && promoCode) {
      setServerError('You can use either a referral code or a promo code, not both.');
      return;
    }
    if (!googleUseName && !isValidRegisteredName(googleFullName)) { setServerError('Enter your real full name using at least two words, with no numbers or symbols.'); return; }
    setGoogleCompletionLoading(true);
    try {
      const res = await api.auth.completeGoogle({
        pendingGoogleSignup: googlePending,
        nameConfirmed: true,
        useGoogleName: googleUseName,
        registeredFullName: googleUseName ? undefined : googleFullName.trim().replace(/\s+/g, ' '),
        deviceId: getInstallationId(),
        referralCode: referralCode || undefined,
        promoCode: promoCode || undefined,
      });
      const data = res.data?.data || {};
      await completeAuthentication(data);
      if (data.securityQuestionsPromptRequired) {
        navigate('/security-questions?mode=prompt', { replace: true, state: { required: false } });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      setServerError(err.response?.data?.message || 'Google signup could not be completed. Please try again.');
    } finally {
      setGoogleCompletionLoading(false);
    }
  };

  if (googlePending) {
    return (
      <div style={{ maxWidth: '440px', margin: '0 auto', padding: '40px 20px 60px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '1.75rem', fontWeight: '700', color: colors.textPrimary, marginBottom: '6px' }}>Complete Your Account</h1>
          <p style={{ fontSize: '14px', color: colors.textSecondary }}>You're almost ready to start playing on 9jaWin.</p>
        </div>
        <section style={{ padding: '14px', marginBottom: '20px', border: '1px solid #2a2a2a', borderRadius: '10px', background: colors.card }}>
          <p style={{ margin: '0 0 8px', color: colors.textSecondary, fontSize: '12px', textTransform: 'uppercase' }}>Name received from Google</p>
          <p style={{ margin: '0 0 14px', color: colors.textPrimary, fontSize: '17px', fontWeight: 700 }}>{googleName || 'Google did not provide a name'}</p>
          <p style={{ margin: '0 0 10px', color: colors.textPrimary }}>Is this your real full name as it appears on your bank account?</p>
          <div style={{ display: 'grid', gap: '8px' }}>
            <button type="button" onClick={() => setGoogleUseName(true)} style={{ ...inputBase, borderColor: googleUseName ? colors.primary : colors.border, textAlign: 'left' }}>{googleUseName ? '✓ ' : ''}Yes, that's my name</button>
            <button type="button" onClick={() => setGoogleUseName(false)} style={{ ...inputBase, borderColor: !googleUseName ? colors.primary : colors.border, textAlign: 'left' }}>{!googleUseName ? '✓ ' : ''}No, let me enter it</button>
          </div>
          {!googleUseName && <div style={{ marginTop: '14px' }}><label style={labelStyle}>Real full name</label><input value={googleFullName} onChange={e => setGoogleFullName(e.target.value)} autoComplete="name" maxLength={120} placeholder="As it appears on your bank account" style={inputBase} /></div>}
          <p style={{ margin: '12px 0 0', color: colors.textMuted, fontSize: '12px', lineHeight: 1.5 }}>{REGISTERED_NAME_NOTICE}</p>
        </section>
        {serverError && <div style={{ background: colors.errorBg, border: `1px solid ${colors.error}`, color: colors.error, padding: '12px 16px', borderRadius: '10px', marginBottom: '20px', fontSize: '14px' }}>⚠️ {serverError}</div>}
        <form onSubmit={handleGoogleCompletion} noValidate>
          <div style={{ marginBottom: fieldGap }}>
            <label style={labelStyle}>Referral Code <span style={{ fontWeight: '400', textTransform: 'none' }}>(optional)</span></label>
            <input type="text" name="referralCode" value={form.referralCode} onChange={handleChange} disabled={Boolean(form.promoCode.trim())} placeholder="Enter a referral code" autoComplete="off" maxLength={20} style={{ ...inputBase, borderColor: colors.border, opacity: form.promoCode.trim() ? 0.55 : 1 }} />
          </div>
          <div style={{ marginBottom: fieldGap }}>
            <label style={labelStyle}>Promo Code <span style={{ fontWeight: '400', textTransform: 'none' }}>(optional)</span></label>
            <input type="text" name="promoCode" value={form.promoCode} onChange={handleChange} disabled={Boolean(form.referralCode.trim())} placeholder="Enter a promo code" autoComplete="off" maxLength={20} style={{ ...inputBase, borderColor: colors.border, opacity: form.referralCode.trim() ? 0.55 : 1 }} />
          </div>
          <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '-8px', marginBottom: fieldGap }}>You can use either a referral code or a promo code, not both.</div>
          <button type="submit" disabled={googleCompletionLoading} style={{ width:'100%', padding:'14px', borderRadius:'10px', border:'none', background:googleCompletionLoading ? '#00b892' : colors.primary, color:'#000', fontSize:'16px', fontWeight:'700', cursor:googleCompletionLoading?'not-allowed':'pointer', opacity:googleCompletionLoading?0.7:1 }}>
            {googleCompletionLoading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
        <button type="button" onClick={() => { setGooglePending(''); setServerError(''); }} style={{ display:'block', margin:'16px auto 0', border:0, background:'transparent', color:colors.textSecondary, fontSize:'14px', cursor:'pointer' }}>Back to registration</button>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '440px',
      margin: '0 auto',
      padding: '40px 20px 60px',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '1.75rem',
          fontWeight: '700',
          color: colors.textPrimary,
          marginBottom: '6px',
        }}>
          <TurnstileWidget onVerify={setCaptchaToken} />
          Create Account
        </h1>
        <p style={{ fontSize: '14px', color: colors.textSecondary }}>
          Join 9jaWin and start playing
        </p>
      </div>

      {/* Server Error Banner */}
      {serverError && (
        <div style={{
          background: colors.errorBg,
          border: `1px solid ${colors.error}`,
          color: colors.error,
          padding: '12px 16px',
          borderRadius: '10px',
          marginBottom: '20px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span>⚠️</span> {serverError}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} noValidate>
        <div style={{ marginBottom: fieldGap }}>
          <label style={labelStyle}>Real Full Name</label>
          <input type="text" name="fullName" value={form.fullName} onChange={handleChange} onBlur={handleBlur} autoComplete="name" maxLength={120} placeholder="As it appears on your bank account" style={{ ...inputBase, borderColor: touched.fullName && errors.fullName ? colors.error : colors.border }} />
          <div style={errorStyle}>{touched.fullName && errors.fullName}</div>
          <p style={{ color: colors.textMuted, fontSize: '12px', lineHeight: 1.5, margin: '8px 0 0' }}>{REGISTERED_NAME_NOTICE}</p>
        </div>
        {/* Username */}
        <div style={{ marginBottom: fieldGap }}>
          <label style={labelStyle}>Username</label>
          <input
            type="text"
            name="username"
            value={form.username}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Choose a username"
            autoComplete="username"
            maxLength={30}
            style={{
              ...inputBase,
              borderColor: touched.username && errors.username ? colors.error : colors.border,
            }}
            onFocus={(e) => {
              e.target.style.borderColor = touched.username && errors.username ? colors.error : colors.borderFocus;
              e.target.style.boxShadow = `0 0 0 3px ${touched.username && errors.username ? 'rgba(255,77,77,0.15)' : 'rgba(0,212,170,0.15)'}`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = colors.border;
              e.target.style.boxShadow = 'none';
              handleBlur(e);
            }}
          />
          <div style={errorStyle}>{touched.username && errors.username}</div>
        </div>

        {/* Email */}
        <div style={{ marginBottom: fieldGap }}>
          <label style={labelStyle}>Email</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="your@email.com"
            autoComplete="email"
            style={{
              ...inputBase,
              borderColor: touched.email && errors.email ? colors.error : colors.border,
            }}
            onFocus={(e) => {
              e.target.style.borderColor = touched.email && errors.email ? colors.error : colors.borderFocus;
              e.target.style.boxShadow = `0 0 0 3px ${touched.email && errors.email ? 'rgba(255,77,77,0.15)' : 'rgba(0,212,170,0.15)'}`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = colors.border;
              e.target.style.boxShadow = 'none';
              handleBlur(e);
            }}
          />
          <div style={errorStyle}>{touched.email && errors.email}</div>
        </div>

        {/* Phone */}
        <div style={{ marginBottom: fieldGap }}>
          <label style={labelStyle}>Phone <span style={{ fontWeight: '400', textTransform: 'none' }}>(optional)</span></label>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={form.country === 'NG' ? '08012345678' : 'Phone number'}
            autoComplete="tel"
            style={{
              ...inputBase,
              borderColor: touched.phone && errors.phone ? colors.error : colors.border,
            }}
            onFocus={(e) => {
              e.target.style.borderColor = touched.phone && errors.phone ? colors.error : colors.borderFocus;
              e.target.style.boxShadow = `0 0 0 3px ${touched.phone && errors.phone ? 'rgba(255,77,77,0.15)' : 'rgba(0,212,170,0.15)'}`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = colors.border;
              e.target.style.boxShadow = 'none';
              handleBlur(e);
            }}
          />
          <div style={errorStyle}>{touched.phone && errors.phone}</div>
        </div>

        {/* Referral Code */}
        <div style={{ marginBottom: fieldGap }}>
          <label style={labelStyle}>Referral Code <span style={{ fontWeight: '400', textTransform: 'none' }}>(optional)</span></label>
          <input
            type="text"
            name="referralCode"
            value={form.referralCode}
            onChange={handleChange}
            disabled={Boolean(form.promoCode.trim())}
            placeholder="Enter a referral code"
            autoComplete="off"
            maxLength={20}
            style={{ ...inputBase, borderColor: colors.border, opacity: form.promoCode.trim() ? 0.55 : 1, cursor: form.promoCode.trim() ? 'not-allowed' : 'text' }}
            onFocus={(e) => {
              e.target.style.borderColor = colors.borderFocus;
              e.target.style.boxShadow = '0 0 0 3px rgba(0,212,170,0.15)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = colors.border;
              e.target.style.boxShadow = 'none';
            }}
          />
          <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '4px' }}>
            Got a referral code? Enter it to earn your friend a first-deposit reward.
          </div>
        </div>

        {/* Promo Code */}
        <div style={{ marginBottom: fieldGap }}>
          <label style={labelStyle}>Promo Code <span style={{ fontWeight: '400', textTransform: 'none' }}>(optional)</span></label>
          <input
            type="text"
            name="promoCode"
            value={form.promoCode}
            onChange={handleChange}
            disabled={Boolean(form.referralCode.trim())}
            placeholder="Enter a promo code"
            autoComplete="off"
            maxLength={20}
            style={{ ...inputBase, borderColor: colors.border, opacity: form.promoCode.trim() ? 0.55 : 1, cursor: form.promoCode.trim() ? 'not-allowed' : 'text' }}
            onFocus={(e) => {
              e.target.style.borderColor = colors.borderFocus;
              e.target.style.boxShadow = '0 0 0 3px rgba(0,212,170,0.15)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = colors.border;
              e.target.style.boxShadow = 'none';
            }}
          />
          <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '4px' }}>
            Have a 9jaWin promo code? Enter it to support your inviter.
          </div>
        </div>

        <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '-8px', marginBottom: fieldGap }}>
          You can use either a referral code or a promo code, not both.
        </div>

        {/* Password */}
        <div style={{ marginBottom: fieldGap }}>
          <label style={labelStyle}>Password</label>
          <input
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Create a strong password"
            autoComplete="new-password"
            style={{
              ...inputBase,
              borderColor: touched.password && errors.password ? colors.error : colors.border,
            }}
            onFocus={(e) => {
              e.target.style.borderColor = touched.password && errors.password ? colors.error : colors.borderFocus;
              e.target.style.boxShadow = `0 0 0 3px ${touched.password && errors.password ? 'rgba(255,77,77,0.15)' : 'rgba(0,212,170,0.15)'}`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = colors.border;
              e.target.style.boxShadow = 'none';
              handleBlur(e);
            }}
          />
          <div style={errorStyle}>{touched.password && errors.password}</div>
          {/* Password hints */}
          {!touched.password && !form.password && (
            <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '6px', lineHeight: '1.5' }}>
              Must be at least 8 characters with uppercase, number, and special character
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div style={{ marginBottom: '24px' }}>
          <label style={labelStyle}>Confirm Password</label>
          <input
            type="password"
            name="confirmPassword"
            value={form.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Re-enter your password"
            autoComplete="new-password"
            style={{
              ...inputBase,
              borderColor: touched.confirmPassword && errors.confirmPassword ? colors.error : colors.border,
            }}
            onFocus={(e) => {
              e.target.style.borderColor = touched.confirmPassword && errors.confirmPassword ? colors.error : colors.borderFocus;
              e.target.style.boxShadow = `0 0 0 3px ${touched.confirmPassword && errors.confirmPassword ? 'rgba(255,77,77,0.15)' : 'rgba(0,212,170,0.15)'}`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = colors.border;
              e.target.style.boxShadow = 'none';
              handleBlur(e);
            }}
          />
          <div style={errorStyle}>{touched.confirmPassword && errors.confirmPassword}</div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '14px',
            borderRadius: '10px',
            border: 'none',
            background: loading ? '#00b892' : colors.primary,
            color: '#000',
            fontSize: '16px',
            fontWeight: '700',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'background 0.2s, opacity 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          {loading ? (
            <>
              Creating Account...
            </>
          ) : (
            'Register'
          )}
        </button>
      </form>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0 4px', color: colors.textSecondary, fontSize: 12 }}>
        <span style={{ flex: 1, height: 1, background: colors.border }} />
        OR
        <span style={{ flex: 1, height: 1, background: colors.border }} />
      </div>

      <GoogleSignInButton
        mode="register"
        referralCode={form.referralCode.trim().toUpperCase()}
        promoCode={form.promoCode.trim().toUpperCase()}
        onSuccess={(data) => {
          if (data?.requiresCompletion && data?.data?.pendingGoogleSignup) {
            const name = String(data.data.googleName || '');
            setGooglePending(data.data.pendingGoogleSignup);
            setGoogleName(name);
            setGoogleFullName(name);
            setGoogleUseName(isValidRegisteredName(name));
            setServerError('');
            return;
          }
          completeAuthentication(data);
          navigate('/', { replace: true });
        }}
        onError={(message) => setServerError(message)}
      />

      {/* Login Link */}
      <p style={{
        textAlign: 'center',
        marginTop: '24px',
        fontSize: '14px',
        color: colors.textSecondary,
      }}>
        Already have an account?{' '}
        <Link to="/login" style={{ color: colors.primary, fontWeight: '600', textDecoration: 'none' }}>
          Login
        </Link>
      </p>
    </div>
  );
}
